import { Feature } from 'geojson';
import {
  COMBINED_HWW_CONST,
  HEALTH_INDEX_CONST,
  WEALTH_INDEX_CONST,
  WISE_INDEX_CONST,
} from '../../../../shared/types/feature-data-type';

/**
 * The letter grade the popup prints, together with the colour the map already
 * painted the cell underneath it.
 *
 * `base` is lifted verbatim from `AllLayers.COMBINED_HWW.colorScale` (see
 * base/layers.ts) so a cell the map drew dark green cannot show up here as a
 * C. `ink` is a darkened companion, because two of the five ramp stops
 * (#DFF365, #FDAE61) are too light to carry text.
 */
export interface HwwGrade {
  letter: string;
  base: string;
  ink: string;
  /** 0-100, how full the sub-index meter draws. */
  fill: number;
}

interface GradeStyle {
  letter: string;
  base: string;
  ink: string;
  fill: number;
}

const F: GradeStyle = {
  letter: 'F',
  base: '#D73027',
  ink: '#B3261E',
  fill: 16,
};
const D: GradeStyle = {
  letter: 'D',
  base: '#FDAE61',
  ink: '#A9660D',
  fill: 37,
};
const C: GradeStyle = {
  letter: 'C',
  base: '#DFF365',
  ink: '#6C8709',
  fill: 58,
};
const B: GradeStyle = {
  letter: 'B',
  base: '#7FCC72',
  ink: '#2F7A3A',
  fill: 79,
};
const A: GradeStyle = {
  letter: 'A',
  base: '#409126',
  ink: '#2C6B1B',
  fill: 100,
};

/**
 * Thresholds mirror `getHwwSymbol` in number-formatter.pipe.ts, which is what
 * the rest of the app grades by. They differ per index on purpose: a health
 * index of 0.95 is a middling area, an income index of 0.95 is a good one.
 */
const THRESHOLDS: { [base: string]: [number, GradeStyle][] } = {
  [HEALTH_INDEX_CONST]: [
    [1.03, A],
    [0.99, B],
    [0.95, C],
    [0.93, D],
  ],
  [WEALTH_INDEX_CONST]: [
    [1.2, A],
    [1.0, B],
    [0.9, C],
    [0.7, D],
  ],
  [WISE_INDEX_CONST]: [
    [1.1, A],
    [0.8, B],
    [0.7, C],
    [0.6, D],
  ],
  [COMBINED_HWW_CONST]: [
    [1.05, { ...A, letter: 'A+' }],
    [0.9, B],
    [0.85, C],
    [0.75, D],
  ],
};

export function hwwGrade(feature: string, value: unknown): HwwGrade | null {
  if (typeof value !== 'number' || !isFinite(value)) return null;

  const base = Object.keys(THRESHOLDS).find((key) => feature.includes(key));
  if (!base) return null;

  const match = THRESHOLDS[base].find(([min]) => value >= min);
  return match ? match[1] : F;
}

/**
 * The four index names the `features` endpoint answers to.
 *
 * The scope lives in the response, not the request: asking for `HEALTH_INDEX`
 * returns `{ byCountry, byState, absolute }`. `MapColoringService` strips the
 * suffix the same way before its bulk call (see `preformatFeatureName`).
 */
export const HWW_REQUEST_FEATURES = [
  HEALTH_INDEX_CONST,
  WEALTH_INDEX_CONST,
  WISE_INDEX_CONST,
  COMBINED_HWW_CONST,
];

interface HwwScopes {
  byCountry?: number | null;
  byState?: number | null;
}

/**
 * Flattens `{ HEALTH_INDEX: { byCountry, byState } }` into the suffixed keys
 * the popup and the map both read (`HEALTH_INDEX_by_country`).
 *
 * The popup needs its own copy because the suffixed values otherwise only
 * exist in `feature.state`, which `MapColoringService` fills in *only for the
 * layer currently colouring the map*. Before this, the score block sat empty
 * until the visitor happened to switch the right-hand menu to Area Summary —
 * the grade was there in the data and invisible on screen.
 *
 * Mirrors `DataFormatter.formatArbitraryCellProperties`, which does the same
 * unpacking for the arbitrary-cell popup.
 */
export function spreadHwwScopes(
  data: Feature['properties']
): Feature['properties'] {
  const spread: { [key: string]: number | null } = {};

  for (const base of HWW_REQUEST_FEATURES) {
    const scopes = data?.[base] as HwwScopes | null | undefined;
    if (!scopes || typeof scopes !== 'object') continue;

    if (scopes.byCountry != null)
      spread[`${base}_by_country`] = scopes.byCountry;
    if (scopes.byState != null) spread[`${base}_by_state`] = scopes.byState;
  }

  return spread;
}
